
import React, { useEffect, useState } from 'react';
import ChartComponent from '../../../components/chart';
import Api from '../../../services/api';
import FilterCohost2 from '../../../components/filterCohost2';
import { Spin } from 'antd';
import moment from 'moment';
import {useParams} from "react-router-dom";
import { toast } from 'react-toastify';
import { END_DATE, START_DATE } from '../../../utility';

const CohortRevenueChart = ({ chartTitle, chartKey, data, loading }) => {
    const [chartData, setChartData] = useState({ labels: [], datasets: [] });

    useEffect(() => {
        const defaultColors = [
            '#3366CC', '#DC3912', '#FF9900', '#109618', '#990099',
            '#0099C6', '#DD4477', '#66AA00', '#B82E2E', '#316395'
        ];

        const chartContent = data?.[chartKey]; 

        if (chartContent && chartContent.labels && chartContent.datasets) {
            setChartData({
                labels: chartContent.labels,
                datasets: chartContent.datasets.map((ds, index) => ({
                    ...ds,
                    borderColor: defaultColors[index % defaultColors.length],
                    backgroundColor: 'transparent',
                    tension: 0.3,
                    pointRadius: 3,
                    borderWidth: 2,
                }))
            });
        }
    }, [data, chartKey]);

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            tooltip: {
                callbacks: {
                    label: function (context) {
                        const label = context.dataset.label || '';
                        const value = context.parsed.y;
                        if (chartKey.includes('roas') || chartKey === 'retention') {
                            return `${label}: ${value}%`;
                        }
                        return `${label}: ${value}`;
                    }
                }
            },
            legend: {
                display: true,
                position: 'right',
                labels: {
                    usePointStyle: true,
                    pointStyle: 'line',
                    padding: 20
                }
            },
            title: {
                display: true,
                text: chartTitle,
                align: 'start',
                font: {
                    size: 16,
                },
            },
        },
        interaction: {
            mode: 'index',
            intersect: false,
        },
        scales: {
            y: {
                min: 0,
                ticks: {
                    stepSize: 15,
                    callback: function (value) {
                        if (chartKey.includes('roas') || chartKey === 'retention') {
                            return value + '%';
                        }
                        return value;
                    },
                },
                title: {
                    display: false,
                    text: `${chartKey.toUpperCase()} Value`,
                }
            },
            x: {
                title: {
                    display: false,
                    text: 'Thời gian (theo tháng)',
                }
            }
        }
    };

    return (
        <div className="mb-5">
            <h3 style={{ marginBottom: "20px" }}>{chartTitle}</h3>
            <div style={{ height: '400px', position: 'relative' }}>
                <Spin spinning={loading} style={{ width: '100%' }}>
                    <ChartComponent type="line" data={chartData} options={chartOptions} />
                </Spin>
            </div>
        </div>
    );
};

const DataTable = ({ data = [], dataTotal = [] , selectedOptions = [] }) => {
    const [sortConfig, setSortConfig] = useState({ field: null, direction: null });
    const safeData = Array.isArray(data) ? data : [];

    const formatNumber = (num) =>
        Number(num || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 });

    const showCountry = selectedOptions.length > 0;

    const sortedData = React.useMemo(() => {
        if (safeData.length === 0 || !sortConfig.field) return safeData;

        const direction = sortConfig.direction === 'asc' ? 1 : -1;

        return [...safeData].sort((a, b) => {
            const aVal = parseFloat(a[sortConfig.field]) || 0;
            const bVal = parseFloat(b[sortConfig.field]) || 0;
            return aVal > bVal ? direction : aVal < bVal ? -direction : 0;
        });
    }, [safeData, sortConfig]);

    const toggleSort = (field) => {
        setSortConfig((prev) => ({
            field,
            direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
        }));
    };


    
    const ltvKeys = Array.from(
        new Set(
            safeData.flatMap(row => row.ltv ? Object.keys(row.ltv) : [])
        )
    ).sort((a, b) => {
        const numA = parseInt(a.replace('M', ''), 10);
        const numB = parseInt(b.replace('M', ''), 10);
        return numA - numB;
    });

    
    const totalRow = ltvKeys.reduce((acc, key) => {
        acc[key] = safeData.reduce((sum, row) => sum + (row.ltv?.[key] || 0), 0);
        return acc;
    }, {});

    console.log(ltvKeys)
    console.log(safeData)


    return (
        <div style={{ overflowX: 'auto' }}>
            <table className="table table-bordered table-hover tb-campaign-center" style={{ minWidth: '1000px', tableLayout: 'fixed' }}>
                <thead className="thead-light">
                <tr>
                    <th className="text-left sticky-col-1" style={{ width: '200px' }}>Month</th>
                    {showCountry &&
                        <th className="text-left sticky-col-2" style={{ width: '200px' }}>Country</th>
                    }
                    <th className={`text-center sticky-col-${showCountry ? 3 : 2}`} style={{ width: '200px' }} onClick={() => toggleSort('cost')}>
                        Cost{' '}
                        <i className={`fa-solid ${
                            sortConfig.field === 'cost'
                                ? sortConfig.direction === 'asc'
                                    ? 'fa-arrow-up text-success'
                                    : 'fa-arrow-down text-success'
                                : 'fa-arrow-up text-muted'
                        }`} />
                    </th>
                    <th className="text-center" style={{ width: '150px' }}>Ad Network Installs</th>
                    <th className="text-center" style={{ width: '150px' }}>Attributed Installs</th>
                    {ltvKeys.map(key => (
                        <th key={key} className="text-center" style={{ width: '150px' }}>{key}</th>
                    ))}
                </tr>
                </thead>
                <tbody>
                {sortedData.map((row, idx) => (
                    <tr key={idx}>
                        <td className="sticky-col-1">{moment(row.month).format('MMM YYYY')}</td>
                        {showCountry && <td className="sticky-col-2">{row.country}</td>}
                        <td className={`text-center sticky-col-${showCountry ? 3 : 2}`}>${formatNumber(row.cost)}</td>
                        <td className="text-center">{formatNumber(row.install_ad_network)}</td>
                        <td className="text-center">{formatNumber(row.install_attributed)}</td>
                        {ltvKeys.map(key => (
                            <td key={key} className="text-center">${formatNumber(row.ltv?.[key])}</td>
                        ))}
                    </tr>
                ))}
                {/* Row tổng */}
                <tr style={{ backgroundColor: '#f9f9f9', fontWeight: 'bold' }}>
                    <td className="sticky-col-1">Total</td>
                    {showCountry && <td className="sticky-col-2">-</td>}
                    <td className={`text-center sticky-col-${showCountry ? 3 : 2}`}>${formatNumber(dataTotal.cost || 0)}</td>
                    <td className="text-center">{formatNumber(dataTotal.install_ad_network || 0)}</td>
                    <td className="text-center">{formatNumber(dataTotal.install_attributed || 0)}</td>
                    {ltvKeys.map(key => (
                        <td key={key} className="text-center">${formatNumber(totalRow[key])}</td>
                    ))}
                </tr>
                </tbody>
            </table>
        </div>
    );
};
const CohostRevenueCharts = () => {

    const [monthRange, setMonthRange] = useState({
        startDate: moment().subtract(5, 'month'),
        endDate: moment(),
    });

    const [filters, setFilters] = useState({
        from_month: moment().subtract(5, "month").format("YYYY-MM"),
        to_month: moment().format("YYYY-MM"),
    });

    const [hasRunReport, setHasRunReport] = useState(false);

    const metric_array = [
        { key: "ad_revenue", name: "Ad Revenue" },
        { key: "iap_revenue", name: "IAP Revenue" },
        { key: "sub_revenue", name: "Subscription Revenue" },
        { key: "total_revenue", name: "Total Revenue" },
    ];

    const [selectedOptions, setSelectedOptions] = useState([]);
    const [selectedOptionName, setSelectedOptionName] = useState(['Ad Revenue', 'Ad ROAS']);
    const [metricFilters, setMetricFilters] = useState('ad_revenue');
    const [loading, setLoading] = useState(false);

    const { code, os } = useParams();

    const [datatable, setDatatable] = useState([]);
    const [dataChart, setDataChart] = useState([]);

    const handleRunReport = async ({ startDate, endDate, selectedOptions, metricFilters, selectedName }) => {
        setLoading(true);

        setSelectedOptionName(selectedName);
        setHasRunReport(true);

        const params = {
            code: code,
            os: os,
            from: monthRange.startDate.format('YYYY-MM-01'), 
            to: monthRange.endDate.format('YYYY-MM-01'), 
            options: selectedOptions.join(','),
            metric: metricFilters,
        };

        Api.get(`/api/app/revenue_cohort`, { params })
            .then(result => {
                if (result.data) {
                    setDataChart(result.data.data_chart);
                    setDatatable(result.data);
                    
                    
                    setSelectedOptions(selectedOptions || []);
                    setMetricFilters(metricFilters || null);
                } else {
                    toast.error("Lỗi lấy dữ liệu!");
                }

            })
            .catch(() => {
                toast.error("Đã có lỗi xảy ra khi gọi API!");
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleMonthRangeChange = (range) => {
        setMonthRange(range);
        setFilters({
            ...filters,
            from_month: range.startDate.format("YYYY-MM"),
            to_month: range.endDate.format("YYYY-MM"),
        });
    };

    return (
        <div>
            <div className="desktop-filter">
                <FilterCohost2
                    monthRange={monthRange}
                    onMonthRangeChange={handleMonthRangeChange}
                    id="filter-tab1"
                    onRunReport={handleRunReport}
                />
            </div>

            {hasRunReport && (
                <>
                    <div className="mt-8">
                        <div className="chart-1">
                            <CohortRevenueChart loading={loading} chartTitle={selectedOptionName[0]} chartKey={`${metricFilters}_revenue`} data={dataChart} />
                        </div>
                    </div>

                    <div className="mt-8">
                        <div className="chart-2">
                            <CohortRevenueChart loading={loading} chartTitle={selectedOptionName[1]} chartKey={`${metricFilters}_roas`} data={dataChart} />
                        </div>
                    </div>
                    <div className="mt-8">
                        <div className="chart-3">
                            <CohortRevenueChart loading={loading} chartTitle="Retention" chartKey="retention" data={dataChart} />
                        </div>
                    </div>

                    <div className="mt-8">
                        <div className="table-wrapper" style={{ position: 'relative' }}>
                            <Spin spinning={loading}>
                              <DataTable data={datatable.data || []} dataTotal={datatable.data_total || {}} selectedOptions={selectedOptions} />
                            </Spin>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default CohostRevenueCharts;
